import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { useContentManager } from '../../hooks/useContentManager';
import useSiteSettings from '../../hooks/useSiteSettings';
import HubSpotForm from '../common/HubSpotForm';

type ContactSectionProps = {
  page?: string; // page key in CMS (e.g., 'home', 'brand', 'amenities', 'collections', 'residences', 'contact')
  showFloatingIcons?: boolean;
};

export default function ContactSection({ page = 'home', showFloatingIcons = true }: ContactSectionProps) {
  const { getContentItem, loading } = useContentManager();
  
  // Admin panelden verileri al
  const getWithFallback = (section: string) =>
    getContentItem(page, section) ||
    getContentItem('brand', section) ||
    getContentItem('amenities', section) ||
    getContentItem('home', section);

  const contactTitle = getWithFallback('contact-title');
  const contactForm = getWithFallback('contact-form');
  const contactLinks = getWithFallback('contact-links');
  const contactSocial = getWithFallback('contact-social');
  const contactIcons = getWithFallback('contact-icons');
  const isBrand = page === 'brand';
  const isBrandStyle = isBrand || page === 'home' || page === 'residences' || page === 'new-releases' || page === 'amenities' || page === 'collections';
  const brandFooter = getContentItem('brand', 'footer');
  
  // Background image with fallbacks: prefer page-specific 'contact-bg', then 'footer' bg, then brand/amenities/home
  const bgItemPage = getContentItem(page, 'contact-bg') || getContentItem(page, 'footer');
  const bgItemBrand = getContentItem('brand', 'contact-bg') || getContentItem('brand', 'footer');
  const bgItemAmenities = getContentItem('amenities', 'contact-bg') || getContentItem('amenities', 'footer');
  const bgItemHome = getContentItem('home', 'contact-bg') || getContentItem('home', 'footer');
  const bgItem = bgItemPage || bgItemBrand || bgItemAmenities || bgItemHome;
  const backgroundUrl =
    (bgItem?.image_url as string | undefined) ||
    (bgItem?.metadata?.bg_image_url as string | undefined) ||
    (bgItem?.content as string | undefined) ||
    'https://readdy.ai/api/search-image?query=modern%20luxury%20skyscraper%20building%20at%20night%20with%20dramatic%20lighting%20and%20city%20skyline%2C%20architectural%20photography%2C%20dark%20moody%20atmosphere%2C%20professional%20real%20estate%20photography&width=1920&height=1080&seq=contact-bg-001&orientation=landscape';

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: ''
  });
  
  // Admin panelden gelen varsayılan ülke kodu
  const defaultCountryCode = contactForm?.metadata?.default_country_code || '+233';
  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  // Brochure download modal state (for floating PDF icon)
  const [isBrochureModalOpen, setBrochureModalOpen] = useState(false);
  const [brochureForm, setBrochureForm] = useState({ name: '', mobile: '', email: '' });
  const [isBrochureSubmitting, setIsBrochureSubmitting] = useState(false);
  const [brochureSubmitStatus, setBrochureSubmitStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    if (!isBrochureModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBrochureModalOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isBrochureModalOpen]);

  // Varsayılan değerler
  const title = contactTitle?.content || 'EXPRESS\nYOUR\nINTEREST';
  const titleLines = title.split('\n');
  // Left title lines (brand-style). Prefer brand footer; fallback to current page's contact-title split by newline
  const brandLeftTitle1 = (brandFooter?.metadata?.left_title_line1)
    || (contactTitle?.content ? (contactTitle.content.split('\n')[0] || 'Connect with') : 'Connect with');
  const brandLeftTitleBold = (brandFooter?.metadata?.left_title_bold)
    || (contactTitle?.content ? (contactTitle.content.split('\n')[1] || 'the brand') : 'the brand');
  
  // Form ayarları
  const derivedFormTwoLine = () => {
    const raw = contactForm?.metadata?.form_title || '';
    if (!raw) return { line1: 'Express', line2: 'Your Interest' };
    const parts = raw.trim().split(/\s+/);
    if (parts.length === 1) return { line1: raw, line2: '' };
    return { line1: parts.slice(0, -1).join(' '), line2: parts.slice(-1)[0] };
  };
  // Form configs: prefer page-specific contact-form metadata if provided; fallback to brand footer when using brand style
  const formTitle = (contactForm?.metadata?.form_title)
    || `${brandFooter?.metadata?.form_title_line1 || 'Express'} ${brandFooter?.metadata?.form_title_bold || 'Your Interest'}`
    || 'REQUEST A CALLBACK';
  const submitButtonText = (contactForm?.metadata?.submit_button_text)
    || (brandFooter?.metadata?.submit_button_text)
    || 'MEET OUR BRAND AMBASSADOR';
  const termsText = (contactForm?.metadata?.terms_text)
    || (brandFooter?.metadata?.terms_text)
    || 'By submitting, you agree to our terms & conditions*';
  
  // Linkler - admin panelden gelen değerler
  // Links: prefer page-specific contact-links; fallback to brand footer
  const link1Text = (contactLinks?.metadata?.link1_text)
    || (brandFooter?.metadata?.left_link1_text)
    || 'FIND A SALES BOUTIQUE';
  const link1Url = (contactLinks?.metadata?.link1_url)
    || (brandFooter?.metadata?.left_link1_url)
    || '#';
  const link2Text = (contactLinks?.metadata?.link2_text)
    || (brandFooter?.metadata?.left_link2_text)
    || 'DISCOVER THE COLLECTION';
  const link2Url = (contactLinks?.metadata?.link2_url)
    || (brandFooter?.metadata?.left_link2_url)
    || '#';

  // Sosyal medya - admin panelden gelen değerler
  const instagramUrl = contactSocial?.metadata?.instagram || '#';
  const facebookUrl = contactSocial?.metadata?.facebook || '#';
  const youtubeUrl = contactSocial?.metadata?.youtube || '#';
  const linkedinUrl = contactSocial?.metadata?.linkedin || '#';
  const snapchatUrl = contactSocial?.metadata?.snapchat || '#';

  // İletişim ikonları - site ayarlarından gelen değerler
  const siteSettings = useSiteSettings();
  const phoneNumber = siteSettings?.phone || '+971123456789';
  const whatsappNumber = siteSettings?.whatsapp || '971123456789';
  const whatsappString = String(whatsappNumber || '');
  const whatsappNumberClean = whatsappString.startsWith('+') ? whatsappString.substring(1) : whatsappString;
  const emailAddress = siteSettings?.email || 'info@goldentower.com';
  const pdfUrl = siteSettings?.pdf_url || '/catalog.pdf';

  // Brochure download handled directly in submit handler to avoid effect loops

  const handleBrochureSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brochureForm.name.trim() || !brochureForm.mobile.trim() || !brochureForm.email.trim()) return;
    setIsBrochureSubmitting(true);
    setBrochureSubmitStatus(null);
    try {
      const response = await api.post('/forms/submit', {
        form_type: 'register_interest',
        name: brochureForm.name,
        mobile: brochureForm.mobile,
        email: brochureForm.email
      });
      if (!response.success) throw new Error(response.error || 'Submission failed');
      // Trigger brochure download immediately and close modal shortly after
      if (pdfUrl) {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setBrochureSubmitStatus('success');
      setBrochureForm({ name: '', mobile: '', email: '' });
      setTimeout(() => setBrochureModalOpen(false), 800);
    } catch (err) {
      console.error(err);
      setBrochureSubmitStatus('error');
    } finally {
      setIsBrochureSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const fullPhone = `${countryCode}${formData.mobile}`;
      
      // 1. API'ye kaydet
      const response = await api.post('/forms/submit', {
        form_type: 'contact',
        subject: page, // track source page in an existing column
        name: formData.name,
        mobile: fullPhone,
        email: formData.email,
        country_code: countryCode
      });

      if (!response.success) {
        console.error('API error:', response.error);
        throw new Error(response.error || 'Submission failed');
      }

      // 2. Readdy form sistemine de gönder
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('mobile', fullPhone);
      formDataToSend.append('email', formData.email);

      await fetch('https://readdy.ai/api/form/d3rvgn1psnv8430eot30', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      setSubmitStatus('success');
      setFormData({ name: '', mobile: '', email: '' });
      setTimeout(() => setSubmitStatus(null), 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <section className="relative min-h-screen bg-[#2a2a35] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </section>
    );
  }

  return (
    <section className={`relative min-h-screen ${isBrandStyle ? 'bg-black' : 'bg-[#2a2a35]'} text-white overflow-hidden`}>
      {/* Background Image */
      }
      <div className="absolute inset-0">
        <img
          src={backgroundUrl}
          alt="Background"
          className={`w-full h-full object-cover ${isBrandStyle ? 'opacity-70' : 'opacity-40'}`}
        />
        <div className={`absolute inset-0 ${isBrandStyle ? 'bg-black/35' : 'bg-gradient-to-r from-[#2a2a35] via-[#2a2a35]/95 to-[#2a2a35]/80'}`}></div>
      </div>

      <div className="relative z-10 container mx-auto px-6 py-20 flex items-center min-h-screen">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 w-full items-center">
          {/* Left Side - Title and Links */}
          <div className="space-y-8">
            {isBrandStyle ? (
              <div className="space-y-2">
                <h2 className="text-3xl lg:text-4xl font-light tracking-wide">{brandLeftTitle1}</h2>
                <h2 className="text-3xl lg:text-4xl font-bold tracking-wide">{brandLeftTitleBold}</h2>
              </div>
            ) : (
              <div className="space-y-4">
                {titleLines.map((line, index) => (
                  <h2 key={index} className="text-5xl lg:text-6xl font-light tracking-wide">
                    {index === titleLines.length - 1 ? (
                      <>
                        {line.slice(0, -1)}
                        <span className="text-[#C5A46D]">{line.slice(-1)}</span>
                      </>
                    ) : (
                      line
                    )}
                  </h2>
                ))}
              </div>
            )}

            <div className="space-y-6 pt-8">
              <a
                href={link1Url}
                className="flex items-center gap-3 text-[#C5A46D] hover:text-white transition-colors duration-300 text-sm tracking-wider group cursor-pointer"
              >
                <span>{link1Text}</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
              </a>
              <a
                href={link2Url}
                className="flex items-center gap-3 text-[#C5A46D] hover:text-white transition-colors duration-300 text-sm tracking-wider group cursor-pointer"
              >
                <span>{link2Text}</span>
                <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
              </a>
            </div>

            {/* Social Media Icons */}
            <div className="flex gap-4 pt-8">
              {instagramUrl !== '#' && (
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-instagram-line text-lg"></i>
                </a>
              )}
              {facebookUrl !== '#' && (
                <a
                  href={facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
              )}
              {youtubeUrl !== '#' && (
                <a
                  href={youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-youtube-fill text-lg"></i>
                </a>
              )}
              {linkedinUrl !== '#' && (
                <a
                  href={linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
              )}
              {snapchatUrl !== '#' && (
                <a
                  href={snapchatUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center hover:bg-white hover:text-black transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-twitter-fill text-lg"></i>
                </a>
              )}
            </div>
          </div>

          {/* Right Side - Form */}
          <div className={`${isBrandStyle ? 'bg-black/55' : 'bg-[#3a3a45]/50'} backdrop-blur-sm p-8 lg:p-12 rounded-lg`}>
            <div className="mb-6">
              {isBrandStyle ? (
                <div className="text-center">
                  <div className="text-2xl lg:text-3xl font-light tracking-wider">{isBrand ? (brandFooter?.metadata?.form_title_line1 || 'Express') : derivedFormTwoLine().line1}</div>
                  <div className="text-2xl lg:text-3xl font-semibold tracking-wider">{isBrand ? (brandFooter?.metadata?.form_title_bold || 'Your Interest') : derivedFormTwoLine().line2}</div>
                </div>
              ) : (
                <h3 className="text-2xl lg:text-3xl font-light tracking-wider text-center">{formTitle}</h3>
              )}
            </div>

            <HubSpotForm />
          </div>
        </div>
      </div>

      {/* Right Side Floating Contact Icons */}
      {showFloatingIcons && (
      <div className="fixed right-6 bottom-32 z-50 flex flex-col gap-4">
        <a
          href={`tel:${phoneNumber}`}
          className="w-12 h-12 rounded-full bg-black border border-white/20 hover:bg-[#C5A46D] hover:border-[#C5A46D] flex items-center justify-center transition-all duration-300 group cursor-pointer"
          title="Call Us"
        >
          <i className="ri-phone-fill text-white text-xl"></i>
        </a>
        <a
          href={`https://wa.me/${whatsappNumberClean}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-full bg-black border border-white/20 hover:bg-[#25D366] hover:border-[#25D366] flex items-center justify-center transition-all duration-300 group cursor-pointer"
          title="WhatsApp"
        >
          <i className="ri-whatsapp-fill text-white text-xl"></i>
        </a>
        <a
          href={`mailto:${emailAddress}`}
          className="w-12 h-12 rounded-full bg-black border border-white/20 hover:bg-[#C5A46D] hover:border-[#C5A46D] flex items-center justify-center transition-all duration-300 group cursor-pointer"
          title="Email Us"
        >
          <i className="ri-mail-fill text-white text-xl"></i>
        </a>
        <button
          onClick={() => setBrochureModalOpen(true)}
          className="w-12 h-12 rounded-full bg-black border border-white/20 hover:bg-[#C5A46D] hover:border-[#C5A46D] flex items-center justify-center transition-all duration-300 group cursor-pointer"
          title="Download Catalog"
        >
          <i className="ri-file-pdf-fill text-white text-xl"></i>
        </button>
      </div>
      )}

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 bg-black/30 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-6 text-center">
          {isBrandStyle ? (
            <p className="text-xs text-gray-400 tracking-wider">©2025 Golden Tower. All rights reserved | Crafted by Berkon Group</p>
          ) : (
            <p className="text-xs text-gray-400 tracking-wider">©2025 GOLDEN TOWER. ALL RIGHTS RESERVED</p>
          )}
        </div>
      </div>

      {isBrochureModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 cursor-pointer"
          onClick={() => setBrochureModalOpen(false)}
        >
          <div
            className="w-full max-w-lg bg-black border border-white/10 rounded-lg p-6 text-white relative cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setBrochureModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            <h3 className="text-xl font-light mb-4">Register to Download</h3>
            <HubSpotForm />
          </div>
        </div>
      )}
    </section>
  );
}
